/**
 * One error shape for every failed request, so callers do not each invent their
 * own way of asking "was that a validation error or a dead server?".
 */
export type ApiErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'conflict'
  | 'rateLimited'
  | 'server'
  | 'unknown'

/** Laravel's validation error body: field name → list of messages. */
export type ValidationErrors = Record<string, string[]>

const TECHNICAL_ERROR_PATTERNS = [
  /SQLSTATE/i,
  /QueryException/i,
  /PDOException/i,
  /ErrorException/i,
  /Fatal error/i,
  /syntax error at or near/i,
  /violates not-null constraint/i,
  /violates foreign key constraint/i,
  /violates unique constraint/i,
  /violates check constraint/i,
  /null value in column/i,
  /relation ".*" does not exist/i,
  /column ".*" does not exist/i,
  /Connection: pgsql/i,
  /Host: /i,
  /DETAIL: Failing row/i,
  /insert into /i,
  /update ".*" set/i,
  /delete from /i,
  /select .* from /i,
  /Illuminate\\/i,
  /Symfony\\/i,
  /Call to undefined/i,
  /Uncaught Exception/i,
  /Internal Server Error/i,
  /Server Error/i,
  /Request failed with status/i,
  /Failed to fetch/i,
  /NetworkError/i,
  /ECONNREFUSED/i,
  /ETIMEDOUT/i,
  /Cannot read propert/i,
  /undefined is not/i,
  /null is not/i,
  /stack trace:/i,
  /#\d+ \//,
  /at (?:async )?[a-zA-Z0-9_$.]+\s*\(/,
]

export function isTechnicalError(message?: string | null): boolean {
  if (!message || typeof message !== 'string') return false
  return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number
  readonly code: string | undefined
  readonly reference: string | undefined
  readonly reason: string | undefined
  readonly details: Record<string, unknown> | undefined
  readonly isRetryable: boolean
  /** Present only when `kind === 'validation'`. */
  readonly errors: ValidationErrors | undefined

  constructor(args: {
    kind: ApiErrorKind
    status: number
    message: string
    code?: string
    reference?: string
    reason?: string
    details?: Record<string, unknown>
    errors?: ValidationErrors
  }) {
    super(args.message)
    this.name = 'ApiError'
    this.kind = args.kind
    this.status = args.status
    this.code = args.code
    this.reference = args.reference
    this.reason = args.reason
    this.details = args.details
    this.isRetryable =
      args.kind === 'network' || args.kind === 'server' || args.kind === 'rateLimited'
    this.errors = args.errors
  }

  get isTechnical(): boolean {
    return this.kind === 'server' || this.status >= 500 || isTechnicalError(this.message)
  }

  /**
   * Translation key for this error, so callers surface a localised message
   * rather than whatever the server happened to say in English.
   */
  get translationKey(): string {
    if (this.kind === 'forbidden') {
      if (this.code === 'module_not_enabled') return 'error.moduleNotEnabled'
      if (this.code === 'group_suspended') return 'error.suspended'
      return 'error.forbidden'
    }

    switch (this.kind) {
      case 'network':
        return 'error.network'
      case 'unauthorized':
        return 'error.unauthorized'
      case 'notFound':
        return 'error.notFound'
      case 'validation':
        return 'error.validation'
      case 'rateLimited':
        return 'error.rateLimited'
      case 'conflict':
        return 'error.conflict'
      case 'server':
        return 'error.generic'
      default:
        return 'error.generic'
    }
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'notFound'
  if (status === 409) return 'conflict'
  if (status === 422) return 'validation'
  if (status === 429) return 'rateLimited'
  if (status >= 500) return 'server'
  return 'unknown'
}

export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  const status = response.status
  const kind = kindForStatus(status)

  let message = response.statusText || 'Request failed'
  let code: string | undefined
  let reference: string | undefined
  let reason: string | undefined
  let details: Record<string, unknown> | undefined
  let errors: ValidationErrors | undefined

  try {
    const body: unknown = await response.clone().json()

    if (typeof body === 'object' && body !== null) {
      const record = body as {
        code?: unknown
        message?: unknown
        reference?: unknown
        reason?: unknown
        details?: unknown
        errors?: unknown
      }

      if (typeof record.code === 'string') {
        code = record.code
      }

      if (typeof record.message === 'string') {
        message = record.message
      }
      if (typeof record.reference === 'string') {
        reference = record.reference
      }
      if (typeof record.reason === 'string') {
        reason = record.reason
      }
      if (typeof record.details === 'object' && record.details !== null) {
        details = record.details as Record<string, unknown>
      }
      if (typeof record.errors === 'object' && record.errors !== null) {
        errors = record.errors as ValidationErrors
      }
    }
  } catch {
    // A non-JSON error body (an HTML 502 from a proxy, say) is normal.
    // The status code already told us what we need.
  }

  if (reference === undefined) {
    reference = response.headers.get('X-Request-Id') ?? undefined
  }

  return new ApiError({
    kind,
    status,
    message,
    ...(code !== undefined ? { code } : {}),
    ...(reference !== undefined ? { reference } : {}),
    ...(reason !== undefined ? { reason } : {}),
    ...(details !== undefined ? { details } : {}),
    ...(errors ? { errors } : {}),
  })
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

export function isModuleNotEnabled(value: unknown): boolean {
  return isApiError(value) && value.code === 'module_not_enabled'
}
